import {Box, Card, CircularProgress, Divider, ListItemIcon, ListItemText, MenuItem, Popover, SvgIcon, Tooltip, Typography} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import IconButton from "../../../base/button/icon_button";
import {
    AddOutlined,
    BusinessOutlined,
    EditOutlined,
    FiberManualRecord,
    InfoOutlined,
    Inventory2Outlined,
    KeyboardArrowRight,
    LocalShippingOutlined,
    MoreVertRounded,
    MoveDownRounded,
    MoveUpRounded,
    PriceCheckOutlined,
    RemoveOutlined,
    WarehouseOutlined
} from "@mui/icons-material";
import ModifyProductAvailabilityDialog from "../../../dialogs/modify-product-availability";
import CreateInventoryTransferDialog from "../../../dialogs/create-transfer-dialog";
import LoadingLottieAnimation from "../../../app-specific/lottie-animations/loading";
import Button from "../../../base/button/button";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import {useHistory} from "react-router-dom";
import ProductOnPurchasePopover from "../../../app-specific/draggable-popover/product-on-purchase-popover";
import SummaryInfoCard from "../../../app-specific/summary-cards/summary-info-card";
import {Switch} from "../../../base/switch";
import {ReactComponent as PartnerWarehouseIcon} from '../../../../../assets/images/warehouse-icon.svg';
import {ReactComponent as SaveIcon} from "../../../../../assets/images/save-rollback/check.svg";
import {formatMoney} from "../../../../../core/services/utils/utils";
import DateUtils from "../../../../../core/services/date-utils";
import {Popover as CustomPopover} from "../../popover";
import classnames from "classnames";
import {Col, Row} from "reactstrap";
import {OutlinedInput} from "../../../base/input/outlined_input";
import WarehouseScheduleManagementDialog from "./warehouse-schedule-management-dialog";
import {convertApiToInternal, SCHEDULE_TYPE_IDS, WEEKDAY_KEYS} from "./schedule-utils";
import WarehouseTaxManagementDialog from "./warehouse-tax-management-dialog";
import ProductOnBackorderPopover from "../../draggable-popover/product-on-backorder-popover";
import EnvService from "../../../../../core/services/env";
import {generateBusinessName, generateWarehouseName} from "../../../../../core/services/utils/name-generator";

const initAvailabilityModificationDialogState = {
    open: false,
    warehouse: null,
    increase: null,
    currentAvailability: 0,
    id: null,
};

const initInventoryTransferDialogState = {
    open: false,
    partNo: null,
    fromWarehouse: null,
    toWarehouse: null,
};

const initWarehouseScheduleDialogState = {
    open: false,
    selectedScheduleId: null,
}

const initManageApplicableTaxesDialogState = {
    open: false,
    partNo: null,
    partId: null,
    warehouseCode: null,
}

export const ProductWarehouseCard = ({
                                         localWarehouse,
                                         spireWarehouse,
                                         setWarehouse,
                                         partInfo,
                                         refreshWarehouses,
                                         shippingCompanies,
                                         customerInfo,
                                         viewOnly = false,
                                         setSchedule: setApiSchedule,
                                         schedule: apiSchedule,
                                     }) => {
    const schedule = useMemo(() => convertApiToInternal(apiSchedule), [apiSchedule]);
    const [availabilityModificationDialogState, setAvailabilityModificationDialogState] = useState(initAvailabilityModificationDialogState);
    const [inventoryTransferDialogState, setInventoryTransferDialogState] = useState(initInventoryTransferDialogState);
    const [warehouseScheduleDialogState, setWarehouseScheduleDialogState] = useState(initWarehouseScheduleDialogState);
    const [manageApplicableTaxesDialogState, setManageApplicableTaxesDialogState] = useState(initManageApplicableTaxesDialogState);
    const [registeringProduct, setRegisteringProduct] = useState(false);
    const [togglingThresholdEnabled, setTogglingThresholdEnabled] = useState(false);
    const isMounted = useIsMounted();
    const history = useHistory();
    const operatorAccessLevels = useAccessLevels();

    const [anchorEl, setAnchorEl] = useState();
    const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
    const {partId, partNo, isSerializable, cost} = partInfo;
    const {customerId, price} = customerInfo ?? {};

    /**
     * Opens the menu.
     * @param {MouseEvent<HTMLElement>} event    The event.
     */
    const openMenu = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    /**
     * Closes the menu.
     */
    const closeMenu = useCallback(() => {
        setAnchorEl(undefined);
    }, []);

    /**
     * Closes the menu and calls the [action] callback.
     *
     * @param {MouseEventHandler<HTMLElement>} action    The action callback.
     * @return {MouseEventHandler<HTMLElement>}          The event handler.
     */
    const closeMenuOnActionSelection = useCallback((action) =>
        (...args) => {
            closeMenu();
            action?.(...args);
        }, [closeMenu])


    /**
     * Resets the [registeringProduct] state when the [partId] changes.
     */
    useEffect(() => {
        setRegisteringProduct(false);
    }, [partId])

    const manageApplicableTaxes = useCallback(() => {
        setManageApplicableTaxesDialogState({
            open: true,
            partNo: partNo,
            partId: partId,
            warehouseCode: localWarehouse?.warehouseCode,
        })
    }, [localWarehouse?.warehouseCode, partId, partNo])

    /**
     * Opens the availability-modification-dialog of the product for the provided availability entry with the "increase" mode
     * @type {(function(*): void)|*}
     */
    const increaseProductAvailability = useCallback(() => {
        setAvailabilityModificationDialogState({
            id: localWarehouse?.warehouseId,
            increase: true,
            open: true,
            warehouse: spireWarehouse,
            currentAvailability: localWarehouse?.availableQty ?? 0,
        });
    }, [localWarehouse?.availableQty, localWarehouse?.warehouseId, spireWarehouse])

    /**
     * Opens the availability-modification-dialog of the product for the provided availability entry with the "decrease" mode
     * @type {(function(*): void)|*}
     */
    const decreaseProductAvailability = useCallback(() => {
        setAvailabilityModificationDialogState({
            id: localWarehouse?.warehouseId,
            increase: false,
            open: true,
            warehouse: spireWarehouse,
            currentAvailability: localWarehouse?.availableQty ?? 0,
        });
    }, [localWarehouse?.availableQty, localWarehouse?.warehouseId, spireWarehouse])

    /**
     * Opens the inventory-transfer-dialog of the product.
     * @type {(function({fromWarehouse?: null, toWarehouse?: null}): void)}
     */
    const openInventoryTransferDialog = useCallback(({fromWarehouse = null, toWarehouse = null}) => {
        setInventoryTransferDialogState({
            open: true,
            partNo,
            fromWarehouse,
            toWarehouse,
        })
    }, [partNo]);

    /**
     * Navigates to the created transfer detail page.
     */
    const onTransferCreated = useCallback((createdTransfer) => {
        if (createdTransfer?.id) {
            history.push(routeFunctions.inventory.inventoryTransfer.single(createdTransfer.id));
        }
    }, [history]);

    /**
     * Registers the product in the warehouse.
     * @type {(function(*): Promise<void>)|*}
     */
    const registerWarehouse = useCallback(async () => {
        const forApi = {
            partId: partId,
            warehouseId: spireWarehouse.id,
        }
        setRegisteringProduct(true);
        const response = await Api.createPartInWarehouse(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await refreshWarehouses();
        }
        if (!isMounted())
            return;
        setRegisteringProduct(false);
    }, [isMounted, partId, refreshWarehouses, spireWarehouse.id])

    /**
     * Sets the warehouse's schedule.
     * @type {(function(*): void)}
     */
    /**
     * Sets the warehouse's schedule.
     * @type {(function(*): void)}
     */
    const setSchedule = useCallback((apiItem, isDeleted = false) => {
        if (!isMounted()) return;
        setApiSchedule?.(apiItem.warehouseId, prevState => {
            let currentSchedule = prevState || [];
            let newScheduleList = [...currentSchedule];
            if (isDeleted) {
                newScheduleList = newScheduleList.filter(item => item.id !== apiItem.id);
            } else {
                const index = newScheduleList.findIndex(item => item.id === apiItem.id);
                if (index > -1) {
                    newScheduleList[index] = apiItem;
                } else {
                    newScheduleList.push(apiItem);
                }
            }
            return newScheduleList;
        })
    }, [setApiSchedule, isMounted]);

    /**
     * The list of warehouse ids that are currently being updated.
     * @type {(function(*, *): Promise<boolean>)|*}
     */
    const updateMinimumRequiredAvailability = useCallback(async (newValue) => {
        const forApi = {
            warehouseId: spireWarehouse.id,
            partNo: partNo,
            minimumRequiredAvailability: Number(newValue),
        }
        // TODO: Implement the updateMinimumRequiredAvailability API call.
        await new Promise(r => setTimeout(r, 1000));
        const response = {
            resultFlag: true,
        };
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            setWarehouse(prevState => ({
                ...prevState,
                localWarehouse: {
                    ...prevState.localWarehouse,
                    minimumRequiredAvailability: forApi.minimumRequiredAvailability,
                },
            }))
        }
        return response?.resultFlag;
    }, [isMounted, partNo, setWarehouse, spireWarehouse.id]);

    /**
     * Toggles the [availability.thresholdEnabled] state.
     */
    const toggleThresholdEnabled = useCallback(async () => {
        setTogglingThresholdEnabled(true);
        // TODO: For now just hide and show, but later use APIs for diabling and enabling??
        setWarehouse(prevState => ({
            ...prevState,
            localWarehouse: {
                ...prevState.localWarehouse,
                thresholdEnabled: !localWarehouse?.thresholdEnabled,
            },
        }));
        setTogglingThresholdEnabled(false);
        return true;
    }, [localWarehouse?.thresholdEnabled, setWarehouse])


    /**
     * Updates the threshold minimum order quantity.
     */
    const updateThresholdMinimumOrderQuantity = useCallback(async (newValue) => {
        const forApi = {
            warehouseId: spireWarehouse.id,
            partId: partId,
            reOrderPoint: localWarehouse?.thresholdReorderPoint ?? 0,
            minimumOrder: Number(newValue),
        }
        const response = await Api.updatePartWarehouse(forApi)
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            setWarehouse(prevState => ({
                ...prevState,
                localWarehouse: {
                    ...prevState.localWarehouse,
                    thresholdMinimumOrderQuantity: forApi.minimumOrder,
                    thresholdEnabled: true,
                },
            }))
        }
        return response?.resultFlag;
    }, [isMounted, localWarehouse?.thresholdReorderPoint, partId, setWarehouse, spireWarehouse.id]);

    /**
     * Updates the threshold reorder point.
     */
    const updateThresholdReorderPoint = useCallback(async (newValue) => {
        const forApi = {
            warehouseId: spireWarehouse.id,
            partId: partId,
            reOrderPoint: Number(newValue),
            minimumOrder: localWarehouse?.thresholdMinimumOrderQuantity ?? 0,
        }
        const response = await Api.updatePartWarehouse(forApi)
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            setWarehouse(prevState => ({
                ...prevState,
                localWarehouse: {
                    ...prevState.localWarehouse,
                    thresholdReorderPoint: forApi.reOrderPoint,
                    thresholdEnabled: true,
                },
            }))
        }
        return response?.resultFlag;
    }, [isMounted, localWarehouse?.thresholdMinimumOrderQuantity, partId, setWarehouse, spireWarehouse.id]);

    /**
     * Updates the order's information.
     *
     * - if the result of the api is successful, sets the new information.
     * @param {any} entry
     * @param {string} newValue
     * @return {boolean}
     */
    const updateWarehouseInfo = useCallback(async (entry, newValue) => {
        switch (entry.id) {
            case 'minimumRequiredAvailability': {
                return await updateMinimumRequiredAvailability(newValue);
            }
            case 'thresholdMinimumOrderQuantity': {
                return await updateThresholdMinimumOrderQuantity(newValue);
            }
            case 'thresholdReorderPoint': {
                return await updateThresholdReorderPoint(newValue);
            }
            default:
                return true;
        }
    }, [updateMinimumRequiredAvailability, updateThresholdMinimumOrderQuantity, updateThresholdReorderPoint]);

    const inventoryTransferProperties = useMemo(() => (
        [
            ...(operatorAccessLevels.packages.bizInventory.minimumRequiredAvailability ? [{
                id: "minimumRequiredAvailability",
                title: 'Min Req Avlb',
                value: localWarehouse?.minimumRequiredAvailability ?? '',
                editable: operatorAccessLevels.packages.bizInventory.manageMinimumRequiredAvailability && !viewOnly,
                clearable: true,
                edit: updateWarehouseInfo,
                className: "mx-0 warehouse-card-input-wrapper"
            }] : []),
        ]
    ), [localWarehouse?.minimumRequiredAvailability, operatorAccessLevels.packages.bizInventory.manageMinimumRequiredAvailability, operatorAccessLevels.packages.bizInventory.minimumRequiredAvailability, updateWarehouseInfo, viewOnly])

    const thresholdProperties = useMemo(() => (
        [
            ...(operatorAccessLevels.packages.bizInventory.viewAvailabilityThresholds ? [
                {
                    id: "thresholdMinimumOrderQuantity",
                    title: 'Min Order Qty',
                    value: localWarehouse?.thresholdMinimumOrderQuantity ?? '',
                    editable: operatorAccessLevels.packages.bizInventory.manageAvailabilityThresholds && !viewOnly && !!setWarehouse,
                    clearable: true,
                    edit: updateWarehouseInfo,
                    className: "mx-0 warehouse-card-input-wrapper"
                },
                {
                    id: "thresholdReorderPoint",
                    title: 'Reorder Point',
                    value: localWarehouse?.thresholdReorderPoint ?? '',
                    editable: operatorAccessLevels.packages.bizInventory.manageAvailabilityThresholds && !viewOnly && !!setWarehouse,
                    clearable: true,
                    edit: updateWarehouseInfo,
                    className: "mx-0 warehouse-card-input-wrapper"
                },
            ] : []),
        ]
    ), [localWarehouse?.thresholdMinimumOrderQuantity, localWarehouse?.thresholdReorderPoint, operatorAccessLevels.packages.bizInventory.manageAvailabilityThresholds, operatorAccessLevels.packages.bizInventory.viewAvailabilityThresholds, setWarehouse, updateWarehouseInfo, viewOnly])

    /**
     * Opens the warehouse schedule management dialog.
     * @type {(function(*): void)|*}
     */
    const openWarehouseScheduleManagementDialog = useCallback((scheduleId) => {
        setWarehouseScheduleDialogState({
            open: true,
            selectedScheduleId: scheduleId,
        });
    }, []);

    const localWarehouseName = EnvService.enableDemoNameMasking ? generateWarehouseName(localWarehouse?.warehouseName) : localWarehouse?.warehouseName;

    return (
        <>
            <div className={'d-flex flex-column'}>
                <div className={classnames('lookup-own-partner-warehouse', {'view-only': viewOnly})}>
                    <div className={'warehouse-card'}>
                        <div className={'header'}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <WarehouseOutlined className={'mr-1'}/>
                                <p>{localWarehouseName ?? ''}</p>
                                {
                                    !localWarehouse?.isComingSoon && !viewOnly &&
                                    <Tooltip title={"Actions"}>
                                        <IconButton onClick={openMenu} edge={'end'} sx={{marginInlineStart: 'auto'}}>
                                            <MoreVertRounded/>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Box>
                            {
                                operatorAccessLevels.packages.sales.warehouseSchedules &&
                                !localWarehouse?.isComingSoon && !!schedule &&
                                <WarehouseSchedule
                                    schedule={schedule}
                                    viewOnly={!(operatorAccessLevels.packages.sales.manageWarehouseSchedules && !!setApiSchedule)}
                                    onEdit={openWarehouseScheduleManagementDialog}
                                />
                            }
                        </div>
                        <div className={'body'}>
                            {
                                registeringProduct &&
                                <div className={'d-flex justify-content-center'}>
                                    <LoadingLottieAnimation width={200} height={200}/>
                                </div>
                            }
                            {
                                !registeringProduct &&
                                localWarehouse?.isComingSoon &&
                                <div className={''}>
                                    <p className={'text-center font-weight-semi-bold'}>
                                        Coming Soon
                                    </p>
                                </div>
                            }
                            {
                                !registeringProduct &&
                                !localWarehouse?.isComingSoon &&
                                <>
                                    <div className={'d-flex justify-content-between'}>
                                        <p className={'font-weight-semi-bold'}>Available:</p>
                                        <p className={'font-weight-semi-bold'}>{localWarehouse?.availableQty ?? 0}</p>
                                    </div>
                                    <div className={'d-flex justify-content-between'}>
                                        <p className={'grey-3'}>Committed:</p>
                                        <p className={'grey-3 font-weight-semi-bold'}>{localWarehouse?.committedQty ?? 0}</p>
                                    </div>
                                    <div className={'d-flex justify-content-between mb-3'}>
                                        <p className={'grey-3 text-disabled'}>On Hand:</p>
                                        <p className={'grey-3 font-weight-semi-bold'}>{localWarehouse?.onHandQty ?? 0}</p>
                                    </div>
                                    <ProductOnBackorderPopover
                                        partNo={partNo}
                                        warehouseCode={localWarehouse?.warehouseCode}
                                        backOrderQty={localWarehouse?.backOrderQty ?? 0}
                                        buttonProps={{className: "w-100 p-0"}}
                                        customerId={customerId}
                                        customerName={customerInfo?.name}
                                    >
                                        <div className={'d-flex justify-content-between w-100'}>
                                            <p>Back Order:</p>
                                            <p className={'font-weight-semi-bold'}>{localWarehouse?.backOrderQty ?? 0}</p>
                                        </div>
                                    </ProductOnBackorderPopover>
                                    <ProductOnPurchasePopover
                                        partNo={partNo}
                                        warehouseCode={localWarehouse?.warehouseCode}
                                        onPurchaseQty={localWarehouse?.onPurchaseQty ?? 0}
                                        shippingCompanies={shippingCompanies}
                                        buttonProps={{
                                            className: "w-100 p-0"
                                        }}
                                    >
                                        <div className={'d-flex justify-content-between w-100'}>
                                            <p>On Purchase:</p>
                                            <p className={'font-weight-semi-bold'}>{localWarehouse?.onPurchaseQty ?? 0}</p>
                                        </div>
                                    </ProductOnPurchasePopover>
                                    {
                                        !!inventoryTransferProperties.length && !viewOnly &&
                                        <SummaryInfoCard
                                            className={'h-auto p-0'}
                                            body={inventoryTransferProperties}
                                        />
                                    }
                                    {
                                        operatorAccessLevels.packages.bizInventory.viewAvailabilityThresholds && !viewOnly &&
                                        <>
                                            <Box className={'d-flex flex-column'}>
                                                <Box sx={{position: 'relative', marginBlockEnd: 2, marginBlockStart: 3}}>
                                                    <Divider absolute sx={{borderStyle: "dashed", borderColor: 'text.disabled', borderBottomWidth: 2, paddingInline: 3,}}/>
                                                    <Typography
                                                        variant={'body2'}
                                                        color={'text.secondary'}
                                                        fontWeight={600}
                                                        sx={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -65%)', backgroundColor: 'background.paper', paddingInline: 2,}}
                                                    >
                                                        Alert Thresholds
                                                    </Typography>
                                                </Box>
                                                <div className={'d-flex justify-content-between mb-2'}>
                                                    <p className={'font-weight-semi-bold'}>Enable:</p>
                                                    {
                                                        operatorAccessLevels.packages.bizInventory.manageAvailabilityThresholds && !viewOnly && !!setWarehouse &&
                                                        <Switch
                                                            color={'primary'}
                                                            checked={localWarehouse?.thresholdEnabled ?? false}
                                                            disabled={togglingThresholdEnabled}
                                                            onChange={toggleThresholdEnabled}
                                                        />
                                                    }
                                                    {
                                                        !(operatorAccessLevels.packages.bizInventory.manageAvailabilityThresholds && !viewOnly && !!setWarehouse) &&
                                                        <p className={'font-weight-semi-bold'}>{localWarehouse?.thresholdEnabled ? 'Yes' : 'No'}</p>
                                                    }
                                                </div>
                                                {
                                                    localWarehouse?.thresholdEnabled && !!thresholdProperties.length &&
                                                    <SummaryInfoCard
                                                        className={'h-auto p-0'}
                                                        body={thresholdProperties}
                                                    />
                                                }
                                            </Box>
                                        </>
                                    }
                                </>
                            }
                            {
                                spireWarehouse?.isMain &&
                                <div className={'default-indicator'}>
                                    Default Warehouse
                                </div>
                            }
                        </div>
                    </div>
                </div>
                {
                    localWarehouse?.availableQty === 0 && !!customerId && !!localWarehouse.warehouseId &&
                    <LostSalesSection
                        partNo={partNo}
                        partId={partId}
                        customerId={customerId}
                        localWarehouse={localWarehouse}
                        price={price}
                    />
                }
                {
                    !!localWarehouse?.isComingSoon && !viewOnly &&
                    <Button
                        className={'mx-3'}
                        variant={'outlined'}
                        size={'small'}
                        disabled={registeringProduct}
                        onClick={() => registerWarehouse()}
                    >
                        {
                            registeringProduct
                                ? "Processing..."
                                : 'Make Available'
                        }
                    </Button>
                }
            </div>
            <Popover
                anchorEl={anchorEl}
                open={open}
                onClose={closeMenu}
                onClick={closeMenu}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                slotProps={{
                    paper: {sx: {backgroundImage: 'none'}}
                }}
            >
                {
                    !localWarehouse?.isComingSoon &&
                    <>
                        {
                            operatorAccessLevels.packages.sales.manageTaxesForProductWarehouses && !!localWarehouse?.warehouseId &&
                            <MenuItem onClick={closeMenuOnActionSelection(() => manageApplicableTaxes())}>
                                <ListItemIcon>
                                    <PriceCheckOutlined color={'primary'}/>
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{sx: theme => ({fontSize: theme.typography.body2.fontSize,})}}
                                    primary={"Manage Applicable Taxes"}
                                />
                            </MenuItem>
                        }
                        {
                            operatorAccessLevels.packages.bizInventory.submitAdjustment && !!localWarehouse?.warehouseId &&
                            <MenuItem onClick={closeMenuOnActionSelection(() => increaseProductAvailability())}>
                                <ListItemIcon>
                                    <AddOutlined color={'primary'}/>
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{sx: theme => ({fontSize: theme.typography.body2.fontSize,})}}
                                    primary={"Increase Availability"}
                                />
                            </MenuItem>
                        }
                        {
                            operatorAccessLevels.packages.bizInventory.submitAdjustment && !!localWarehouse?.warehouseId &&
                            <MenuItem onClick={closeMenuOnActionSelection(() => decreaseProductAvailability())}>
                                <ListItemIcon>
                                    <RemoveOutlined color={'error'}/>
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{sx: theme => ({fontSize: theme.typography.body2.fontSize,})}}
                                    primary={"Decrease Availability"}
                                />
                            </MenuItem>
                        }
                        {
                            operatorAccessLevels.packages.bizInventory.submitTransfer &&
                            <MenuItem
                                onClick={closeMenuOnActionSelection(() => openInventoryTransferDialog({fromWarehouse: spireWarehouse}))}>
                                <ListItemIcon>
                                    <MoveDownRounded color={'secondary'}/>
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{sx: theme => ({fontSize: theme.typography.body2.fontSize,})}}
                                    primary={"Transfer From"}
                                />
                            </MenuItem>
                        }
                        {
                            operatorAccessLevels.packages.bizInventory.submitTransfer &&
                            <MenuItem
                                onClick={closeMenuOnActionSelection(() => openInventoryTransferDialog({toWarehouse: spireWarehouse}))}>
                                <ListItemIcon>
                                    <MoveUpRounded color={'secondary'}/>
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{sx: theme => ({fontSize: theme.typography.body2.fontSize,})}}
                                    primary={"Transfer To"}
                                />
                            </MenuItem>
                        }
                    </>
                }
            </Popover>
            {
                operatorAccessLevels.packages.bizInventory.submitAdjustment &&
                <ModifyProductAvailabilityDialog
                    open={availabilityModificationDialogState.open}
                    close={() => setAvailabilityModificationDialogState(initAvailabilityModificationDialogState)}
                    warehouse={availabilityModificationDialogState.warehouse}
                    serialized={isSerializable}
                    increase={availabilityModificationDialogState.increase}
                    currentAvailability={availabilityModificationDialogState.currentAvailability}
                    partNo={partNo}
                    setNewAvailability={() => refreshWarehouses()}
                    alreadyAppliedCost={cost}
                />
            }
            {
                operatorAccessLevels.packages.bizInventory.submitTransfer &&
                <CreateInventoryTransferDialog
                    open={inventoryTransferDialogState.open}
                    close={() => setInventoryTransferDialogState(initInventoryTransferDialogState)}
                    initialFromWarehouse={inventoryTransferDialogState.fromWarehouse}
                    initialToWarehouse={inventoryTransferDialogState.toWarehouse}
                    onCreated={onTransferCreated}
                />
            }
            {
                operatorAccessLevels.packages.sales.manageWarehouseSchedules && setApiSchedule &&
                <WarehouseScheduleManagementDialog
                    warehouse={spireWarehouse}
                    open={warehouseScheduleDialogState.open}
                    close={() => setWarehouseScheduleDialogState(initWarehouseScheduleDialogState)}
                    schedule={schedule}
                    selectedScheduleId={warehouseScheduleDialogState.selectedScheduleId}
                    saveSchedule={setSchedule}
                />
            }
            {
                operatorAccessLevels.packages.sales.manageTaxesForProductWarehouses &&
                <WarehouseTaxManagementDialog
                    open={manageApplicableTaxesDialogState.open}
                    close={() => setManageApplicableTaxesDialogState(initManageApplicableTaxesDialogState)}
                    partNo={manageApplicableTaxesDialogState.partNo}
                    partId={manageApplicableTaxesDialogState.partId}
                    warehouseCode={manageApplicableTaxesDialogState.warehouseCode}
                />
            }
        </>
    );
}

const LostSalesSection = ({partNo, partId, customerId, localWarehouse, price}) => {
    const [loading, setLoading] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [lostSaleQuantity, setLostSaleQuantity] = useState('1');
    const [lostSaleUnitPrice, setLostSaleUnitPrice] = useState(price?.toString() ?? '');
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    /**
     * Resets the [lostSaleQuantity] and [alreadySubmitted] state when the part, warehouse, or customer changes.
     */
    useEffect(() => {
        setLostSaleQuantity('1');
        setAlreadySubmitted(false);
    }, [partId, localWarehouse?.warehouseId, customerId]);

    /**
     * Resets the lost sale unit price when the part, warehouse or customer changes.
     */
    useEffect(() => {
        setLostSaleUnitPrice(price?.toString() ?? '');
    }, [price, partId, customerId]);

    /**
     * Reports a lost sales api call in the server.
     */
    const reportLostSale = useCallback(async (onLeave) => {
        setLoading(true);
        const quantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(lostSaleQuantity) : parseInt(lostSaleQuantity)
        const forApi = {
            partNumber: partNo,
            partId: partId,
            customerId: customerId,
            warehouseId: localWarehouse?.warehouseId,
            quantity: quantity ?? 0,
            price: parseFloat(lostSaleUnitPrice) ?? 0,

        }
        const response = await Api.submitLostSale(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            onLeave();
            setAlreadySubmitted(true);
            setLostSaleQuantity('1');
        }
    }, [isMounted, partNo, partId, customerId, localWarehouse, lostSaleQuantity, lostSaleUnitPrice, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    return (
        <>
            <CustomPopover
                elevation={2}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
                child={(onEnter) => (
                    <Button
                        disabled={loading || alreadySubmitted}
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'text'}
                        color={'primary'}
                        onClick={onEnter}
                    >
                        {
                            alreadySubmitted &&
                            <>
                                <p className='mr-1'>Lost Sale Submitted</p>
                                <SaveIcon className={classnames(`save-icon-new`)}/>
                            </>
                        }
                        {
                            !alreadySubmitted &&
                            <>
                                <p className='mr-1'>Lost Sale</p>
                                <KeyboardArrowRight/>
                            </>
                        }
                    </Button>
                )}
                popoverChild={(_, onLeave) => (
                    <Row className='no-gutters align-items-center justify-content-center pt-3 pb-1 px-2'
                         style={{maxWidth: '15rem'}}>
                        <Col xs={12} className={'px-2 mb-4'}>
                            <p className={'text-sm text-center text-color-1'}>
                                Please Select the lost quantity and submit the lost sales report.
                            </p>
                        </Col>
                        <Col xs={12} className={'px-2 mb-4'}>
                            <OutlinedInput
                                onChange={(newValue) => {
                                    while (newValue.charAt(0) === '0') newValue = newValue.substring(1);
                                    setLostSaleUnitPrice(parseFloat(newValue) >= 0 ? newValue : '0' ?? '')
                                }}
                                label={'Unit Price'}
                                name={'unitPrice'}
                                type={'number'}
                                value={lostSaleUnitPrice ?? '0'}
                            />
                        </Col>
                        <Col xs={12} className={'px-2 mb-2'}>
                            <OutlinedInput
                                onChange={(newValue) => {
                                    while (newValue.charAt(0) === '0') newValue = newValue.substring(1);
                                    const quantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(newValue) : parseInt(newValue);
                                    setLostSaleQuantity(quantity >= 0 ? newValue : '0' ?? '0');
                                }}
                                label={'Quantity (optional)'}
                                name={'quantity'}
                                type={'number'}
                                value={lostSaleQuantity ?? '0'}
                            />
                        </Col>
                        {
                            operatorAccessLevels.packages.sales.submitLostSales &&
                            <Col xs={12} className={'px-2 row no-gutters align-items-center justify-content-center'}>
                                <Button
                                    disabled={loading || alreadySubmitted || !lostSaleQuantity || !lostSaleUnitPrice || parseFloat(lostSaleUnitPrice) <= 0 || parseInt(lostSaleQuantity) <= 0}
                                    className={'d-flex align-items-center justify-content-center'}
                                    variant={'text'}
                                    color={'primary'}
                                    onClick={() => reportLostSale(onLeave)}
                                >
                                    <p className='mr-1'>
                                        {
                                            loading
                                                ? "Submitting..."
                                                : "Submit Report"
                                        }
                                    </p>
                                    {
                                        loading
                                            ? <CircularProgress size={20} color={'primary'}/>
                                            : <KeyboardArrowRight/>
                                    }
                                </Button>
                            </Col>
                        }
                    </Row>
                )}
            />
        </>
    );
}


export const ProductOtherPartnerAvailabilityCard = ({partner, price, schedules}) => {
    const operatorAccessLevels = useAccessLevels();

    const partnerName = EnvService.enableDemoNameMasking ? generateBusinessName(partner?.partnerName) : partner?.partnerName;

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', paddingBlockEnd: 3, paddingInline: 2, width: '100%'}} className={'lookup-warehouse-row'}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <SvgIcon className={'icon small'} component={PartnerWarehouseIcon}/>
                        <Typography variant={'body1'} sx={{textAlign: 'start', fontWeight: 600, color: 'text.primary', paddingBlockEnd: 1,}}>
                            {partnerName ?? ""}
                        </Typography>
                    </Box>
                    {
                        operatorAccessLevels.packages.sales.pricesInLookup && !!price &&
                        <p className='text-right text-md font-weight-semi-bold primary-main'>
                            {formatMoney(price)}
                        </p>
                    }
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, paddingInlineStart: 3.5}}>
                    {
                        partner?.warehouses?.map(warehouse => {
                            const warehouseName = EnvService.enableDemoNameMasking ? generateWarehouseName(warehouse?.warehouseName) : warehouse?.warehouseName;
                            return (
                                <Tooltip arrow followCursor key={warehouseName ?? ""} title={warehouse?.altText}>
                                    <Box component={'span'} className={'lookup-warehouse-row'} sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                                        <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1}}>
                                            <Typography variant={'body2'} sx={{textAlign: 'start', fontWeight: 400, color: 'text.primary'}}>
                                                {warehouseName ?? ""}
                                            </Typography>
                                            {
                                                operatorAccessLevels.packages.sales.warehouseSchedules && !!schedules?.[warehouse?.id] &&
                                                <WarehouseSchedule
                                                    schedule={schedules[warehouse?.id]}
                                                    viewOnly
                                                />
                                            }
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                            <Typography
                                                variant={'body2'}
                                                sx={{textAlign: 'start', color: warehouse?.colorStatusCode, fontWeight: 500,}}
                                            >
                                                {warehouse?.title ?? ''}
                                            </Typography>
                                            <InfoOutlined style={{fill: warehouse?.colorStatusCode, height: 18, width: 18}}/>
                                        </Box>
                                    </Box>
                                </Tooltip>
                            )
                        })
                    }
                </Box>
            </Box>
        </>
    );
}

const WarehouseSchedule = ({schedule, viewOnly, onEdit}) => {
    const [now, setNow] = useState(DateUtils.serverNow());

    /**
     * Updates the current time every minute to keep the status indicators accurate.
     */
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(DateUtils.serverNow());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const WEEKDAYS = WEEKDAY_KEYS;
    const scheduleTypes = React.useMemo(() => [
        {id: SCHEDULE_TYPE_IDS.TAKING_ORDER, icon: BusinessOutlined, title: 'Business Hours'},
        {id: SCHEDULE_TYPE_IDS.DELIVERY, icon: LocalShippingOutlined, title: 'Delivery'},
        {id: SCHEDULE_TYPE_IDS.PICKUP, icon: Inventory2Outlined, title: 'Pickup'},
    ], []);

    /**
     * Fetches the status of the schedule type for today.
     * @param typeId
     * @returns {{status: string, color: string}}
     */
    const getStatus = (typeId) => {
        const item = schedule?.find(s => s.scheduleId === typeId);

        // Grey if not set
        if (!item) return {status: 'not-set', color: 'text.secondary'};

        const todayStr = now.format('YYYY-MM-DD');
        const dayKey = WEEKDAYS[now.day()];

        // Check for exception
        const exception = item.exceptions?.find(e => DateUtils.server(e.date).isSame(now, 'day'));

        const hours = exception ?? item.times?.[dayKey];

        if (!hours) {
            return {status: 'not-set', color: 'text.secondary'};
        }

        if (!hours.startTime && !hours.endTime) {
            return {status: 'closed', color: 'error.main'};
        }

        const sTimeStr = hours.startTime;
        const eTimeStr = hours.endTime;

        if (!sTimeStr || !eTimeStr) return {status: 'closed', color: 'error.main'};

        const startDateTime = DateUtils.server(`${todayStr}T${sTimeStr}`);
        const endDateTime = DateUtils.server(`${todayStr}T${eTimeStr}`);

        if (now.isSameOrAfter(startDateTime) && now.isBefore(endDateTime)) {
            // Open. Check closing soon (within 1 hour)
            if (endDateTime.diff(now, 'minute') <= 60) {
                return {status: 'closing-soon', color: 'warning.main'};
            }
            return {status: 'open', color: 'success.main'};
        }

        return {status: 'closed', color: 'error.main'};
    };


    /**
     * Fetches the next 7 days schedule for the given type.
     * @param typeId
     * @returns {{day: *, hours: *}[]|*[]}
     */
    const getNext7Days = (typeId) => {
        const item = schedule?.find(s => s.scheduleId === typeId);
        if (!item) return [];

        // 1. Generate 7 Local Days (buckets)
        const localDays = [];
        const localNow = DateUtils.localNow();
        for (let i = 0; i < 7; i++) {
            localDays.push({
                date: localNow.add(i, 'day'),
                hours: 'N/A', // Default to N/A if not set
                normalHours: null,
            });
        }

        // 2. Scan a window of Server Days to find shifts
        // We scan from serverNow - 1 day to +8 days to cover all potential timezone shifts
        for (let i = -1; i < 9; i++) {
            const serverDate = now.add(i, 'day');
            const serverDateStr = serverDate.format('YYYY-MM-DD');
            const dayKey = WEEKDAYS[serverDate.day()];

            const exception = item.exceptions?.find(e => DateUtils.server(e.date).isSame(serverDate, 'day'));
            const hours = exception ?? item.times?.[dayKey];
            const normalHoursRaw = item.times?.[dayKey];

            // If hours is null/undefined (and no exception), skipping ensures it stays 'N/A' (Not Set)
            // But if we have an Explicit Closed ({null, null}), we must mark it Closed.

            // Helper to format local range
            const formatRange = (s, e) => {
                const start = DateUtils.server(`${serverDateStr}T${s}`);
                const end = DateUtils.server(`${serverDateStr}T${e}`);
                const localStart = DateUtils.serverToLocal(start);
                const localEnd = DateUtils.serverToLocal(end);
                return {
                    localDate: localStart,
                    text: `${localStart.format('HH:mm')} - ${localEnd.format('HH:mm')}`
                };
            };

            // 1. Calculate Normal Text if Exception exists
            let normalText = null;
            if (exception && normalHoursRaw) {
                if (normalHoursRaw.startTime && normalHoursRaw.endTime) {
                    const normal = formatRange(normalHoursRaw.startTime, normalHoursRaw.endTime);
                    normalText = normal.text;
                } else if (normalHoursRaw.startTime === null && normalHoursRaw.endTime === null) {
                    normalText = 'Closed';
                }
            }

            const isExplicitClosed = hours && hours.startTime === null && hours.endTime === null;
            const isOpen = hours && hours.startTime && hours.endTime;

            let bucketIndex = -1;

            if (isOpen) {
                // Case 1: Active Shift -> Use Start Time for Bucket
                const {localDate, text} = formatRange(hours.startTime, hours.endTime);
                bucketIndex = localDays.findIndex(bucket => bucket.date.isSame(localDate, 'day'));

                if (bucketIndex !== -1) {
                    localDays[bucketIndex].hours = text;
                }
            } else {
                // Case 2: Closed or N/A -> Use Noon Strategy for Bucket
                const noonServer = DateUtils.server(`${serverDateStr}T12:00:00`);
                const noonLocal = DateUtils.serverToLocal(noonServer);
                bucketIndex = localDays.findIndex(bucket => bucket.date.isSame(noonLocal, 'day'));

                if (bucketIndex !== -1) {
                    if (isExplicitClosed) {
                        // Only overwrite if it wasn't already set by a previous conflicting shift (Open shifts win)
                        // But since we iterate days, collisions are rare unless shifts span midnight?
                        // "N/A" check protects against overwriting an Open shift from valid overlaps?
                        // Just to be safe, only overwrite if N/A.
                        if (localDays[bucketIndex].hours === 'N/A') {
                            localDays[bucketIndex].hours = 'Closed';
                        }
                    }
                    // If it's effectively N/A (neither open nor closed), we leave it as N/A.
                }
            }

            // Apply Normal Hours if we found a bucket and have normal text
            if (bucketIndex !== -1 && normalText) {
                localDays[bucketIndex].normalHours = normalText;
            }
        }

        return localDays.map(d => ({
            day: d.date.format('ddd'),
            hours: d.hours,
            normalHours: d.normalHours,
        }));
    };

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-start', marginBlockStart: 1}}>
            {scheduleTypes.map((type) => {
                const {status, color} = getStatus(type.id);
                const nextDays = getNext7Days(type.id);
                const TypeIcon = type.icon;

                return (
                    <Tooltip
                        key={type.id}
                        arrow
                        title={
                            <Box sx={{padding: 0.5, minWidth: 200}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', marginBlockEnd: 1}}>
                                    <Card variant={'outlined'} sx={{padding: 0.5, paddingInline: 1, backgroundColor: 'background.default', width: 'fit-content'}}>
                                        <Typography variant="caption" sx={{fontWeight: 700}}>
                                            {type.title}: <Typography variant="caption" sx={{fontWeight: 700, color: color}}>
                                            {
                                                status === 'not-set' ? 'Not Set' :
                                                    status === 'closed' ? 'Closed' :
                                                        status === 'closing-soon' ? 'Closing Soon' : 'Open'
                                            }
                                        </Typography>
                                        </Typography>
                                    </Card>
                                    {
                                        !viewOnly &&
                                        <IconButton size={'small'} color={'paper.primary'} onClick={() => onEdit(type.id)}>
                                            <EditOutlined sx={{fontSize: 16}}/>
                                        </IconButton>
                                    }
                                </Box>
                                {
                                    status === 'not-set' &&
                                    <Typography variant={'caption'} component={'div'} sx={{marginBlock: 5, fontWeight: 500, width: '100%'}} textAlign={'center'}>
                                        Schedule not set
                                    </Typography>
                                }
                                {
                                    status !== 'not-set' &&
                                    <Box sx={{display: 'flex', flexDirection: 'column', paddingInlineStart: 0.5}}>
                                        <Typography variant="body2" sx={{mb: 1, fontWeight: 700}}>
                                            Daily Hours
                                        </Typography>
                                        <Box component="table" sx={{width: '100%', borderSpacing: '8px 2px'}}>
                                            <Box component={'tbody'}>
                                                {nextDays.map((day, idx) => (
                                                    <Box component={'tr'} key={idx}>
                                                        <Box component={'td'} sx={{fontWeight: 600, paddingRight: 1}}>
                                                            {day.day}
                                                        </Box>
                                                        <Box component={'td'} sx={{whiteSpace: 'nowrap'}}>
                                                            {day.hours}
                                                        </Box>
                                                        <Box component={'td'} sx={{fontStyle: 'italic', paddingInlineStart: 1, whiteSpace: 'nowrap'}}>
                                                            {day.normalHours ? `(normally ${day.normalHours})` : ''}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                }
                            </Box>
                        }
                    >
                        <Card
                            variant={'outlined'}
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: 3,
                                backgroundColor: 'background.default',
                                padding: 0.25,
                                paddingInline: 1,
                            }}
                        >
                            <TypeIcon sx={{color: 'primary.main', fontSize: 20}}/>
                            <FiberManualRecord sx={{color: color, fontSize: 15}}/>
                        </Card>
                    </Tooltip>
                );
            })}
        </Box>
    );
};